import AlertDialog, {
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogClose,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/lib/components/ui/alert-dialog/alert-dialog";
import { AdaptiveMenuItem } from "@/lib/components/ui/adaptive-menu/adaptive-menu";
import Trash2 from "@/lib/components/ui/icons/trash-2";
import Spinner from "@/lib/components/ui/spinner";
import { log } from "@soulbyte/core";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { useDeleteThread } from "../../threads.api";
import { removePinnedThread } from "../../threads.pin";
import type { Thread } from "../../threads.utils";

export function ThreadsListDelete({
  thread,
  assistantId,
}: {
  thread: Thread;
  assistantId: string;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  const threadId = searchParams.get("threadId") ?? undefined;

  const deleteThread = useDeleteThread({
    onSuccess: async () => {
      // Remove from pinned threads if it was pinned
      if (thread.id && assistantId) {
        await removePinnedThread(thread.id, assistantId);
      }

      // If the deleted thread is currently open, navigate to the assistant main page
      if (threadId === thread.id) {
        navigate(`/chat?assistantId=${assistantId}`);
      }
    },
  });

  function handleDeleteThread() {
    if (!thread.id || !assistantId) {
      log.error("Cannot delete thread: Thread or assistant ID is missing", {
        extra: {
          thread,
          assistantId,
        },
        disableTracking: true,
      });
      return;
    }
    if (!deleteThread.isEntitled) return;

    deleteThread.mutate({
      params: {
        path: {
          assistant_id: assistantId,
          thread_id: thread.id,
        },
      },
    });
  }

  if (!deleteThread.isEntitled) {
    return null;
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <AdaptiveMenuItem
          variant="critical"
          onSelect={(event) => event.preventDefault()}
        >
          <Trash2 /> {t("threads_action_delete")}
        </AdaptiveMenuItem>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogClose />
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("threads_delete_confirm_title")}
          </AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogClose asChild>
            <AlertDialogCancel>{t("common_cancel")}</AlertDialogCancel>
          </AlertDialogClose>
          <AlertDialogAction
            variant="critical"
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              event.preventDefault();
              handleDeleteThread();
            }}
            disabled={deleteThread.isPending}
          >
            {deleteThread.isPending && <Spinner />}
            {t("threads_delete_action")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
